/**
 * Which speaker a meeting should use by default.
 *
 * With Bluetooth earbuds or a headset connected, that is where the sound belongs
 * - not the phone's loudspeaker. The person can switch to the speaker whenever
 * they like, and that choice is respected until the situation changes: a new
 * headset connecting takes the sound again, and one disconnecting hands it back
 * to the phone.
 *
 * A browser tells a page only a label for each output ("Pixel Buds Pro",
 * "Speakerphone", "AirPods"), so a headset is recognised by its name. Where the
 * browser lets a page choose an output at all (see SpeakerMenu); elsewhere the
 * phone itself already follows a connected Bluetooth device.
 */

// Words that mean "something worn on the head or in the ear".
const WORN = /bluetooth|airpods|buds|earbud|earphone|headphone|headset|hands-?free/i;
// ...and words that mean the phone's own outputs, whatever else the label says.
const BUILT_IN = /speaker|loudspeaker|earpiece|receiver|built-?in/i;

/** Is this output a headset or earbuds rather than the device's own speaker? */
export const isWornOutput = (device) => {
    const label = String(device?.label || '');
    if (!label || device?.deviceId === 'default' || device?.deviceId === 'communications') return false;
    return WORN.test(label) && !BUILT_IN.test(label);
};

/** The headset or earbuds to prefer, if one is connected. */
export const preferredOutput = (outputs = []) => outputs.find(isWornOutput) || null;

/**
 * What to do now that the list of outputs is `outputs`.
 *
 * @param outputs    every output the browser offers now
 * @param knownIds   the ids that were on the previous list (empty the first time)
 * @param currentId  the output in use ('' means the browser's default)
 * @param userChose  whether the person picked an output by hand
 * @returns { switchTo: id|null, resetChoice: boolean }
 */
export const planOutput = ({ outputs, knownIds, currentId, userChose }) => {
    const ids = new Set(outputs.map((o) => o.deviceId));
    const best = preferredOutput(outputs);

    // The output in use has gone (the headset was switched off or taken out):
    // hand the sound back, to another headset if there is one, else the default.
    if (currentId && !ids.has(currentId)) {
        return { switchTo: best ? best.deviceId : 'default', resetChoice: true };
    }

    // A headset has just connected: it takes the sound, even over a speaker the
    // person picked earlier - they have just put it on.
    if (best && !knownIds.has(best.deviceId) && best.deviceId !== currentId) {
        return { switchTo: best.deviceId, resetChoice: true };
    }

    // Starting out with one already connected, and nothing chosen by hand.
    if (best && !userChose && !currentId) {
        return { switchTo: best.deviceId, resetChoice: false };
    }

    return { switchTo: null, resetChoice: false };
};
